import { useCallback, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Loader2, Plus, X, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DetailsDrawer, DetailsDrawerBody } from '@/components/details-drawer';
import { cn } from '@/lib/utils';

export function FormDrawer({
  title,
  description,
  icon,
  widthClassName,
  bodyClassName,
  submitLabel,
  pendingLabel,
  submitHint = 'Cmd/Ctrl+Enter to create',
  submitIcon: SubmitIcon = Plus,
  canSubmit,
  pending,
  open,
  onClose,
  onSubmit,
  onReset,
  resetLabel = 'Clear form',
  children,
  error,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon: LucideIcon;
  widthClassName?: string;
  bodyClassName?: string;
  submitLabel: string;
  pendingLabel?: string;
  submitHint?: string;
  submitIcon?: LucideIcon;
  canSubmit: boolean;
  pending: boolean;
  open?: boolean;
  onClose: () => void;
  onSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  onReset?: () => void;
  resetLabel?: string;
  children: ReactNode;
  error?: ReactNode;
}) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLFormElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        if (canSubmit) event.currentTarget.requestSubmit();
      }
    },
    [canSubmit],
  );

  return (
    <DetailsDrawer
      title={title}
      description={description}
      icon={icon}
      open={open}
      onClose={onClose}
      widthClassName={cn('sm:max-w-[620px] md:max-w-[720px]', widthClassName)}
    >
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit} onKeyDown={handleKeyDown}>
        <DetailsDrawerBody className={cn('space-y-4', bodyClassName)}>
          {children}
          {error}
        </DetailsDrawerBody>
        <div className="flex flex-col-reverse gap-3 border-t border-border px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                disabled={pending}
                className="inline-flex items-center gap-1.5 rounded-sm text-[12px] text-primary transition-colors hover:underline disabled:pointer-events-none disabled:opacity-50"
              >
                <X className="h-3 w-3" aria-hidden />
                {resetLabel}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="hidden text-[10px] text-muted-foreground sm:inline">{submitHint}</span>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" className="gap-1.5" disabled={!canSubmit || pending}>
              {pending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <SubmitIcon className="h-3.5 w-3.5" aria-hidden />
              )}
              {pending ? (pendingLabel ?? submitLabel) : submitLabel}
            </Button>
          </div>
        </div>
      </form>
    </DetailsDrawer>
  );
}
